import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Input, InputContainer, Loading, Dropdown } from "components/common";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { parseCsv } from "utils";
import {
  addBatchName,
  addEmailRecords,
  addMissingColumns,
  setSender,
} from "state/reducers/emailBatching";
import CheckmarkIcon from "assets/CheckmarkIcon";
import MarketingButtons from "../utils/components/MarketingButtons";
import MarketingNavigation from "../utils/components/MarketingNavigation";
import EmailMarketingStepper from "../utils/components/email/EmailMarketingStepper";
import MarketingToolTip from "../utils/components/MarketingToolTip";
import template from "../utils/templates/sms_batch_template.csv";

// TODO: Pull list of senders from backend
// https://docs.sendgrid.com/api-reference/sender-verification/get-all-verified-senders
const SENDERS = [
  "noreply@tomocredit.com",
  "help@tomocredit.com",
  "invitation@tomomarketplace.com",
  "invitation@tomo.deals",
  "collections@tomocredit.com",
];

const EmailFileUpload = () => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bypassed, setBypassed] = useState(false);
  const dispatch = useDispatch();
  const {
    versionDetails: { variables: templateVariables = [] },
    batchName,
    missingColumns,
    sender,
  } = useSelector((state) => state.emailBatching);
  const handleBlob = async (blobFile) => {
    return new Promise((resolve, reject) => {
      const responseReader = new FileReader();
      responseReader.onload = () => {
        return resolve(responseReader.result);
      };
      responseReader.onerror = () => {
        return reject(responseReader.error);
      };
      responseReader.readAsText(blobFile);
    });
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    console.log("accepted file", acceptedFiles);
    if (!acceptedFiles.length) { return; }

    try {
      setLoading(true);
      setFile(acceptedFiles[0]);
      const droppedCsv = await handleBlob(acceptedFiles[0]);
      // Do whatever you want with the file contents
      const parsedCsv = await parseCsv(droppedCsv);
      setSuccess(true);

      if (parsedCsv.length) {
        const csvColumns = Object.keys(parsedCsv[0]);
        const newMissingColumns = ["email", ...templateVariables].filter(
          (templateVariable) => !csvColumns.includes(templateVariable),
        );
        if (newMissingColumns.length) {
          dispatch(addMissingColumns(newMissingColumns));
        }
        dispatch(addEmailRecords(parsedCsv));
      }
      setError("");
      setLoading(false);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ onDrop, accept: ".csv" });

  const removeFile = () => {
    setFile(null);
    setBypassed(null);
    setError("");
    dispatch(addMissingColumns([]));
    dispatch(addEmailRecords([]));
    setSuccess(false);
  };
  const handleInput = (e) => {
    setError("");
    const invalid = /\/|\s|\\/;
    if (invalid.test(e.target.value)) {
      setError("space and slash characters are not allowed");
    } else {
      dispatch(addBatchName(e.target.value));
    }
  };
  return (
    <div className="flex flex-col h-full">
      <MarketingNavigation>
        <MarketingToolTip>
          <small className="text-black my-auto">
            <i className="fas fa-exclamation-circle mr-2 text-gray-600" />
            Questions on formatting?
            <i className="fas fa-file-download ml-2 mr-2" />
            <a style={{ color: "#5daf23" }} href={template} download>
              Download CSV template
            </a>
          </small>
        </MarketingToolTip>
      </MarketingNavigation>
      <EmailMarketingStepper />
      <p className="font-semibold">1. Choose sender</p>
      <Dropdown
        id="dropdown1"
        elements={SENDERS.map((s) => ({ value: s, name: s }))}
        selectedElement={{ value: sender, name: sender }}
        didSelect={(e) => {
          dispatch(setSender(e.value));
        }}
        className="px-3 py-2 max-w-lg"
        componentProps={{ className: "py-1" }}
      />
      <p className="font-semibold mt-8">2. Add batch name</p>
      <div
        className={`${
          loading ? "cursor-not-allowed" : ""
        } flex flex-col h-full`}
      >
        {error && <p className="text-red-600">{error}</p>}
        <InputContainer className="mt-2">
          <Input
            placeholder="Batch name"
            value={batchName || ""}
            onChange={handleInput}
            type="tel"
          />
        </InputContainer>
        <div className="mt-8">
          <p className="font-semibold">3. Drop a CSV file below</p>
          <div className="">
            {!success ? (
              <div>
                <p className="inline">Please include the following columns: </p>
                <p className="inline italic font-semibold">
                  {["email", ...templateVariables].join(", ")}
                </p>
              </div>
            ) : null}
          </div>
        </div>
        {missingColumns.length ? (
          <div>
            <div className="text-red-600">
              <p className="inline pr-1">
                {`The CSV does not include the column${
                  missingColumns.length > 1 ? "s" : ""
                }`}
              </p>
              <p className="font-semibold inline">
                {missingColumns.join(", ")}
              </p>
            </div>
            <div className="flex items-center ml-auto">
              <input
                type="checkbox"
                className="align-middle mr-2"
                id="accounts"
                defaultChecked={bypassed}
                // checked={bypassed}
                onChange={() => {
                  setBypassed(!bypassed);
                }}
              />
              <label htmlFor="accounts" className="pr-1">
                Bypass
              </label>
            </div>
          </div>
        ) : null}
        {success && !missingColumns.length ? (
          <div className="flex">
            <CheckmarkIcon className="pr-1" fill="#48bb78" />
            <p className="font-semibold text-green-500">
              The CSV file has all the required files
            </p>
          </div>
        ) : null}
        {loading ? <Loading /> : null}
        {file && success ? (
          <div className="flex mt-4 items-center">
            <i className="fas fa-file-csv fa-lg pr-1" />
            <p>{file.name}</p>
            <i
              className="fas fa-times fa-lg pr-1 ml-2 hover:text-red-500"
              onClick={removeFile}
            />
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={classnames(
              "h-full mt-4 flex justify-center cursor-pointer rounded-lg",
              {
                "border-dashed border-gray-600 border": !isDragActive,
                "border-4 border-blue-500": isDragActive,
              },
            )}
          >
            <input {...getInputProps()} className="w-full h-full" />
            {isDragActive ? (
              <div className="flex items-center flex-col justify-center">
                <i className="fas fa-cloud-download-alt fa-5x pr-1 mb-2 text-gray-500" />
                <p>Drop here</p>
              </div>
            ) : (
              <div className="flex items-center flex-col justify-center">
                <i className="fas fa-cloud-download-alt fa-5x pr-1 mb-2 text-gray-500" />
                <p>Drag 'n' drop a CSV file here, or click to select files</p>
              </div>
            )}
          </div>
        )}
      </div>
      <MarketingButtons
        prevButton="Previous"
        prevLink="/marketing/email"
        nextButton="Continue"
        nextLink="/marketing/email/review"
        required={
          !success || !batchName || (missingColumns.length && !bypassed)
        }
      />
    </div>
  );
};

export default EmailFileUpload;
